import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";
import type { NewsCategory } from "@/types";

const timeOptions = ["最近一周", "最近一个月", "全部"] as const;
const typeOptions = ["全部", "资讯", "影评", "行业动态"] as const;
const PAGE_SIZE = 6;

type TimeOption = (typeof timeOptions)[number];
type TypeOption = (typeof typeOptions)[number];

function withinDays(dateStr: string, days: number) {
  const d = new Date(dateStr).getTime();
  const now = new Date("2025-10-15").getTime();
  return now - d <= days * 24 * 60 * 60 * 1000;
}

export default function CategoryNews() {
  useSeo({
    title: "最新电影资讯 - 91电影",
    description:
      "91电影最新电影资讯栏目，聚合国内外上映动态、深度影评与行业观察，支持按时间与类型筛选，快速获取你关心的电影资讯。",
    keywords: "电影资讯,最新电影资讯,影评,行业动态,91电影",
  });

  const [time, setTime] = useState<TimeOption>("全部");
  const [type, setType] = useState<TypeOption>("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    return news.filter((item) => {
      if (type !== "全部" && item.category !== (type as NewsCategory)) return false;
      if (time === "最近一周" && !withinDays(item.date, 7)) return false;
      if (time === "最近一个月" && !withinDays(item.date, 30)) return false;
      return true;
    });
  }, [time, type]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setTime("全部");
    setType("全部");
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 animate-fade-up">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影资讯" }]} />

      <div className="mt-5 mb-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          最新电影资讯
        </h1>
        <p className="text-sm md:text-base text-foreground-500 mt-2 max-w-2xl">
          聚合国内外上映动态、深度影评与行业观察，按时间与类型快速筛选你关心的电影资讯。
        </p>
      </div>

      <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 mb-8 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground-700 shrink-0">
            <i className="ri-calendar-line text-foreground-500" />
            时间
          </span>
          <div className="flex flex-wrap gap-2">
            {timeOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  setTime(opt);
                  setPage(1);
                }}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  time === opt
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
        <div className="h-px bg-background-200" />
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground-700 shrink-0">
            <i className="ri-price-tag-3-line text-foreground-500" />
            类型
          </span>
          <div className="flex flex-wrap gap-2">
            {typeOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  setType(opt);
                  setPage(1);
                }}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  type === opt
                    ? "bg-accent-500 text-secondary-950 font-bold"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>

      {pageItems.length === 0 ? (
        <EmptyState
          icon="ri-filter-off-line"
          title="暂无符合条件的资讯"
          description="试试调整筛选条件，或重置筛选查看更多内容。"
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md px-5 py-2.5 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pageItems.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>

          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  p === safePage
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}